<!-- filepath: d:\programming\vuejs-components\Vc Website Components\src\components\queryapp\WeatherChartDashboard.vue -->
<template>
  <div class="weather-chart-dashboard" >
    
     <!-- Quick Actions -->
    <div class="d-flex justify-content-between align-items-center mb-2 gap-2">
        <div>
            <h3 class="mb-0">
                <i class="bi bi-bar-chart-line-fill me-2"></i> Weather Dashboard (beta)
            </h3>
            <button class="btn btn-sm text-primary" @click="showFeedback()">
                <i class="bi bi-chat-dots me-1"></i> Provide feedback
            </button>
        </div>
       
      <div class="d-flex align-items-center gap-2 ms-auto">
        <!-- Edit Mode Toggle -->
        <div class="form-check form-switch d-flex align-items-center m-0">
          <input 
            class="form-check-input mt-0 me-2" 
            type="checkbox" 
            role="switch" 
            id="editModeToggle"
            v-model="editModeEnabled">
          <label class="form-check-label  mb-0" for="editModeToggle">
            <span v-if="editModeEnabled" class="badge bg-warning text-dark">
            <i class="bi bi-unlock"></i> Edit mode
            </span>
            <span v-else class="badge bg-secondary">
            <i class="bi bi-lock"></i> View mode
            </span>
          </label>
        </div> 
      </div>
      
      <button type="button" class="btn btn-outline-secondary" @click="takeScreenshot()">
        <i class="bi bi-camera"></i>
        <span class="d-none d-lg-inline"> {{ isGeneratingScreenshot ? ' Generating...' : ' Screenshot' }}</span>
      </button>
    </div>
    
      <!-- Control Panel -->
    <div v-if="editModeEnabled" class="chart-controls-container mt-3 mb-2">
        <div class="card">
          <div class="card-body p-3">
            <!-- Primary Actions Row -->
            <div class="d-flex justify-content-end align-items-center mb-3 gap-2">
              <div class="btn-group" role="group">
                <button type="button" class="btn btn-primary btn-sm" @click="addNewChart">
                  <i class="bi bi-plus-circle me-1"></i> Add Chart
                </button>
                <button type="button" class="btn btn-outline-danger btn-sm" @click="clearDashboard"
                  :disabled="charts.length === 0">
                  <i class="bi bi-trash me-1"></i> Clear All
                </button>
              </div>
              <!--
              <div class="text-muted small">
                <span v-if="charts.length > 0">
                  {{ charts.length }} chart{{ charts.length !== 1 ? 's' : '' }}
                </span>
                <span v-else>No charts</span>
              </div>
-->
              <button class="btn btn-sm btn-link text-decoration-none p-0" 
                type="button" 
                @click="showAdvanced = !showAdvanced">
                <i :class="showAdvanced ? 'bi bi-chevron-up' : 'bi bi-chevron-down'" class="me-1"></i>
                <span class="small">{{ showAdvanced ? 'Hide' : 'Show' }} Advanced Options</span>
              </button>
            </div>

            <!-- Advanced Options Toggle -->
            <div class="border-top ">
              <!-- Advanced Options Content -->
              <Transition name="slide">
                <div v-show="showAdvanced" class="mt-3">
                  <div class="row g-3">
                    <!-- Layout Options -->
                    <div class="col-md-6 col-lg-3">
                      <div class="d-flex flex-column gap-2">
                        <label class="form-label small mb-2 fw-semibold">Layout</label>
                        
                        <div class="input-group input-group-sm">
                          <label class="input-group-text">
                            <i class="bi bi-grid-3x3-gap"></i>
                          </label>
                          <select v-model="gridColumns" class="form-select" @change="updateGridLayout">
                            <option :value="1">1 Column</option>
                            <option :value="2">2 Columns</option>
                            <option :value="3">3 Columns</option>
                            <option :value="4">4 Columns</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    <!-- Interactive Options -->
                    <div class="col-md-6 col-lg-3">
                      <div class="d-flex flex-column gap-2">
                        <label class="form-label small mb-2 fw-semibold">Interaction</label>
                        
                        <div class="form-check form-switch small">
                          <input class="form-check-input" type="checkbox" 
                            v-model="enableDragDrop" id="enableDragDrop">
                          <label class="form-check-label" for="enableDragDrop">
                            <i class="bi bi-arrows-move me-1"></i> Enable Reordering
                          </label>
                        </div>
                        

                      </div>
                    </div>

                    <!-- Display Options -->
                    <div class="col-md-6 col-lg-3">
                      <div class="d-flex flex-column gap-2">
                        <label class="form-label small mb-2 fw-semibold">Display</label>
                        
 
                        
                        <div class="form-check form-switch small">
                          <input class="form-check-input" type="checkbox" 
                            v-model="enableTooltip" id="enableTooltip">
                          <label class="form-check-label" for="enableTooltip">
                            <i class="bi bi-chat-square-dots me-1"></i> Show Tooltips
                          </label>
                        </div>
                      </div>
                    </div>

                    <!-- Export Options -->
                    <div class="col-md-6 col-lg-3">
                      <div class="d-flex flex-column gap-2 align-items-start ">
                        <label class="form-label small mb-2 fw-semibold">Support</label>
                        
                        <button type="button" class="btn btn-link btn-sm text-muted align-start p-0 m-0 border-0" 
                          @click="exportDashboard" :disabled="charts.length === 0">
                          <i class="bi bi-download me-1"></i> Export Debug Config
                        </button>
                        
                      </div>
                    </div>
                  </div>
                </div>
              </Transition>
            </div>
          </div>
        </div>
    </div>
    <!-- Chart Grid -->
    <div class="chart-grid" :class="`grid-cols-${gridColumns}`" >
      <div v-if="charts.length === 0" class="text-center py-5">
        <i class="bi bi-graph-up display-4 text-muted"></i>
        <p class="text-muted mt-3">No charts added yet. Select options above and click "Add" to create a chart.</p>
      </div>
      
      <div v-else class="chart-grid-container" id="dashboardcontent">
        <TransitionGroup name="chart-list" tag="div" class="row">
          <div v-for="(chart, index) in charts" 
            :key="chart.id"
            :class="getChartClass(chart)"
            :style="getChartStyle(chart)"
            :draggable="enableDragDrop && !chart.editing"
            @dragstart="handleDragStart($event, index)"
            @dragover.prevent="handleDragOver($event, index)"
            @drop="handleDrop($event, index)"
            @dragend="handleDragEnd" class="mb-3">
            
            <div class="chart-container card h-100" 
              :class="{ 
                'dragging': draggedIndex === index,
                'drag-over': dragOverIndex === index,
                'editing': chart.editing,
                'new-chart': chart.isNew
              }">
              

              <div class="card-header d-flex justify-content-between align-items-center" :class="{'flex-wrap': isCompactChart(chart)}">
                <!-- Normal header when not editing -->
                <template v-if="!chart.editing">
                    <!-- Left side: Drag handle and title -->
                    <div class="d-flex align-items-center" style="min-width: 0; flex: 1 1 auto;">
                    <!-- Drag Handle (inline with title) -->
                    <div v-if="editModeEnabled && enableDragDrop && !chart.editing && chartTitleEditIndex !== index" 
                        class="drag-handle-inline noscreenshot me-2" 
                        title="Drag to reorder">
                        <i class="bi bi-grip-vertical"></i>
                    </div>
                    
                    <!-- Editable Title -->
                    <h6 class="mb-0 d-flex align-items-center" style="min-width: 0; flex: 1 1 auto;">
                        <i v-if="!isCompactChart(chart)" :class="getChartIcon(chart)" class="me-2"></i>
                        
                        <!-- Title editing mode -->
                        <template v-if="chartTitleEditIndex === index && editModeEnabled">
                            <input 
                                type="text" 
                                class="form-control form-control-sm me-2"
                                style="max-width: 300px;"
                                v-model="chart.title"
                                @keyup.enter="saveTitleEdit(index)"
                                @keyup.escape="cancelTitleEdit(index)"
                                @blur="saveTitleEdit(index)"
                                ref="titleInput"
                                placeholder="Enter chart title">
                            <button 
                                class="btn btn-sm btn-success me-1" 
                                @click="saveTitleEdit(index)"
                                title="Save title">
                                <i class="bi bi-check"></i>
                            </button>
                            <button 
                                class="btn btn-sm btn-secondary" 
                                @click="cancelTitleEdit(index)"
                                title="Cancel">
                                <i class="bi bi-x"></i>
                            </button>
                        </template>
                        
                        <!-- Display mode -->
                        <template v-else>
                        <span class="chart-title text-truncate" @dblclick="editModeEnabled && startEditTitle(index)" :title="'Double-click to edit title'">
                            {{ getChartTitle(chart) }}
                        </span>
                        <button v-if="editModeEnabled"
                            class="btn btn-link btn-sm text-muted ms-2 p-0 noscreenshot" 
                            @click="startEditTitle(index)"
                            title="Edit title">
                            <i class="bi bi-pencil-square" style="font-size: 0.875rem;"></i>
                        </button>
                        </template>
                        
                       
                       
                    </h6>
                  </div>

                  <div v-if="editModeEnabled" class="noscreenshot flex-shrink-0">
                    <!-- For larger charts, show inline buttons -->
                    <div  class="btn-group btn-group-sm">
                        <!-- Resize toggle button (only show in custom layout mode) -->
                        <button v-if="layoutMode === 'custom'" 
                        type="button" 
                        class="btn btn-outline-secondary btn-sm" 
                        :class="{ 'active': chart.showResizeControls }"
                        @click="toggleResizeControls(index)" 
                        :title="chart.showResizeControls ? 'Hide resize controls' : 'Show resize controls'">
                        <i class="bi bi-aspect-ratio"></i>
                        </button>
                        <template v-if="layoutMode === 'custom' && chart.showResizeControls">
                            <button class="btn btn-outline-secondary btn-sm" @click="resizeChart(index, 'width', -1)" 
                            :disabled="chart.colSpan <= 1" title="Decrease width">
                            <i class="bi bi-arrow-bar-left"></i>
                            </button>
                            <span class="badge bg-secondary mx-1">{{ chart.colSpan }}×{{ chart.rowSpan }}</span>
                            <button class="btn btn-outline-secondary btn-sm" @click="resizeChart(index, 'width', 1)"
                            :disabled="chart.colSpan >= gridColumns" title="Increase width">
                            <i class="bi bi-arrow-bar-right"></i>
                            </button>
                            <button class="btn btn-outline-secondary btn-sm" @click="resizeChart(index, 'height', -1)"
                            :disabled="chart.rowSpan <= 1" title="Decrease height">
                            <i class="bi bi-arrow-bar-up"></i>
                            </button>
                            <button class="btn btn-outline-secondary btn-sm" @click="resizeChart(index, 'height', 1)"
                            :disabled="chart.rowSpan >= 3" title="Increase height">
                            <i class="bi bi-arrow-bar-down"></i>
                            </button>
                        </template>
                        <template v-else>
                            <!-- Edit button -->
                            <button type="button" class="btn btn-outline-secondary btn-sm" 
                            @click="startEditChart(index)" title="Edit chart">
                            <i class="bi bi-pencil"></i>
                            </button>
                            <!-- Move buttons -->
                            <button v-if="!enableDragDrop" type="button" class="btn btn-outline-secondary btn-sm" 
                            @click="moveChart(index, -1)" :disabled="index === 0" title="Move left">
                            <i class="bi bi-arrow-left"></i>
                            </button>
                            <button v-if="!enableDragDrop" type="button" class="btn btn-outline-secondary btn-sm" 
                            @click="moveChart(index, 1)" :disabled="index === charts.length - 1" title="Move right">
                            <i class="bi bi-arrow-right"></i>
                            </button>
                            
                            
                            <button type="button" class="btn btn-outline-secondary btn-sm" 
                            @click="duplicateChart(index)" title="Duplicate">
                            <i class="bi bi-files"></i>
                            </button>
                            <button type="button" class="btn btn-outline-secondary btn-sm" 
                            @click="removeChart(index)" title="Remove">
                            <i class="bi bi-x"></i>
                            </button>
                        </template>
                    </div>
                    
                    </div>
                    <!-- Minimal action menu when edit mode is disabled -->
                    <div v-else class="btn-group btn-group-sm noscreenshot">
                        <button type="button" class="btn btn-outline-secondary btn-sm" 
                        @click="downloadChart(chart.id)" title="Download">
                        <i class="bi bi-download"></i>
                        </button>
                        <button type="button" class="btn btn-outline-secondary btn-sm" 
                        @click="toggleFullscreen(chart.id)" title="Fullscreen">
                        <i class="bi bi-arrows-fullscreen"></i>
                        </button>
                    </div>
                </template>
                
                <!-- Edit mode header -->
                <template v-else>
                  <h6 class="mb-0">
                    <i class="bi bi-pencil-square text-primary"></i> 
                    {{ chart.isNew ? 'New Chart Configuration' : 'Edit Chart' }}
                  </h6>
                  <div class="btn-group btn-group-sm">
                    <button type="button" class="btn btn-success btn-sm" 
                      @click="saveChartEdits(index)" 
                      :disabled="!chart.editElements || chart.editElements.length === 0"
                      title="Save changes">
                      <i class="bi bi-check-lg"></i> {{ chart.isNew ? 'Create' : 'Save' }}
                    </button>
                    <button type="button" class="btn btn-secondary btn-sm" 
                      @click="cancelChartEdit(index)" title="Cancel">
                      <i class="bi bi-x-lg"></i> Cancel
                    </button>
                  </div>
                </template>
              </div>
              
              <!-- Chart body -->
              <div class="card-body" :style="getChartBodyStyle(chart)">
                <!-- Edit form when in edit mode -->
                <div v-if="chart.editing" class="chart-edit-form">
                  <div class="row g-2">
                    <!-- Period selection -->
                    <div class="col-12">
                      <label class="form-label small">Time Period</label>
                      <div class="btn-group btn-group-sm w-100" role="group">
                        <button v-for="period in availablePeriods" :key="period"
                          type="button" 
                          class="btn btn-outline-primary"
                          :class="{ active: chart.editPeriod === period }"
                          @click="chart.editPeriod = period">
                          {{ period.charAt(0).toUpperCase() + period.slice(1) }}
                        </button>
                      </div>
                    </div>

                    
                     <!-- Elements selection with chart type per element -->
                    <div class="col-12">
                        <label class="form-label small">
                            Elements Configuration
                            <span class="badge bg-secondary ms-1">{{ chart.editElements.length }} selected</span>
                        </label>
                        <div class="element-config-list border rounded p-2" >
                        <!-- Selected elements with individual settings -->
                        <div v-if="chart.editElements.length > 0" class="mb-2">
                        <div v-for="elementId in chart.editElements" :key="elementId" 
                            class="element-config-item border rounded p-2 mb-2">
                            <div class="d-flex justify-content-between align-items-center">
                            <div class="d-flex align-items-center flex-grow-1">
                                <i :class="getElementIcon(elementId)" class="me-2"></i>
                                <strong>{{ getElementLabel(elementId) }}</strong>
                            </div>
                            <button class="btn btn-sm btn-outline-danger" 
                                    @click="removeElementFromChart(chart, elementId)">
                                <i class="bi bi-x"></i>
                            </button>
                            </div>
                            
                            <!-- Per-element settings - collapsible -->
                            <div class="element-settings mt-2">
                                <div class="row g-2">
                                    <div class="col-3">
                                        <select class="form-select form-select-sm" 
                                                v-model="chart.editElementConfigs[elementId].chartType"
                                                title="Display type">
                                            <option value="line">📈 Line</option>
                                            <option value="bar">📊 Bar</option>
                                            <option value="area">📉 Area</option>
                                            <option value="scatter">⚪ Scatter</option>
                                        </select>
                                    </div>
                                    <div class="col-3">
                                        <select class="form-select form-select-sm" 
                                                v-model="chart.editElementConfigs[elementId].axis"
                                                title="Y-Axis">
                                            <option value="left">← Left</option>
                                            <option value="right">Right →</option>
                                        </select>
                                    </div>
                                    <div class="col-3">
                                        <div class="input-group input-group-sm">
                                            <input type="color" 
                                                class="form-control form-control-color form-control-sm"
                                                v-model="chart.editElementConfigs[elementId].color"
                                                :title="`Color for ${getElementLabel(elementId)}`">
                                            <button class="btn btn-outline-secondary btn-sm" 
                                                    @click="resetElementColor(chart, elementId)"
                                                    title="Reset to default">
                                                <i class="bi bi-arrow-counterclockwise"></i>
                                            </button>
                                        </div>
                                    </div>
                                    <div class="col-3">
                                        <div class="form-check form-switch small pt-1">
                                            <input class="form-check-input" type="checkbox" 
                                                :id="`stats-${chart.id}-${elementId}`"
                                                v-model="chart.editElementConfigs[elementId].showStats">
                                            <label class="form-check-label" :for="`stats-${chart.id}-${elementId}`">
                                                Stats
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        </div>
        
                       <!-- Add Element Dropdown -->
                        <div class="add-element-dropdown position-relative" v-if="chart.editElements.length < 5">
                            <div class="input-group">
                                <span class="input-group-text">
                                    <i class="bi bi-plus-circle"></i>
                                </span>
                                <input 
                                    type="text" 
                                    class="form-control form-control-sm" 
                                    placeholder="Click to add elements or type to search..."
                                    v-model="elementSearchQuery"
                                    @focus="showElementDropdown = true"
                                    @blur="handleElementDropdownBlur"
                                    :disabled="chart.editElements.length >= 5">
                                <button 
                                    class="btn btn-outline-secondary btn-sm" 
                                    type="button"
                                    @click="showElementDropdown = !showElementDropdown"
                                    :disabled="chart.editElements.length >= 5">
                                    <i :class="showElementDropdown ? 'bi bi-chevron-up' : 'bi bi-chevron-down'"></i>
                                </button>
                            </div>
                            
                            <!-- Dropdown menu -->
                            <div v-show="showElementDropdown" 
                                class="dropdown-menu show w-100 shadow">
                                <!-- Quick add common elements -->
                                <div v-if="!elementSearchQuery" class="px-3 py-2 border-bottom">
                                    <small class="text-muted d-block mb-2 fw-semibold">Quick Add:</small>
                                    <div class="d-flex flex-wrap gap-1">
                                        <button 
                                            v-for="elementId in getQuickAddElements()" 
                                            :key="elementId"
                                            class="btn btn-sm btn-outline-primary"
                                            :disabled="chart.editElements.includes(elementId)"
                                            @mousedown.prevent="() => addElementToChart(chart, elementId)">
                                            <i :class="getElementIcon(elementId)" class="me-1"></i>
                                            {{ getElementLabel(elementId) }}
                                        </button>
                                    </div>
                                </div>
                                
                                <!-- Grouped elements -->
                                <div class="overflow-auto" style="max-height: 280px;">
                                    <template v-for="(group, groupName) in getFilteredElementGroups()" :key="groupName">
                                        <div v-if="group && group.length > 0">
                                            <h6 class="dropdown-header bg-light">
                                                <i class="bi bi-folder2-open me-1"></i>{{ groupName }}
                                            </h6>
                                            <button 
                                                v-for="element in group" 
                                                :key="element.id"
                                                class="dropdown-item d-flex align-items-center justify-content-between"
                                                :class="{ 'disabled': chart.editElements.includes(element.id) }"
                                                :disabled="chart.editElements.includes(element.id)"
                                                @mousedown.prevent="() => addElementToChart(chart, element.id)">
                                                <span>
                                                    <i :class="getElementIcon(element.id)" class="me-2"></i>
                                                    {{ element.label }}
                                                </span>
                                                <span v-if="chart.editElements.includes(element.id)" 
                                                    class="badge bg-success ms-auto">
                                                    <i class="bi bi-check"></i>
                                                </span>
                                            </button>
                                        </div>
                                    </template>
                                </div>
                                
                                <!-- No results message -->
                                <div v-if="elementSearchQuery && !hasFilteredElements()" 
                                    class="text-center text-muted py-3">
                                    <i class="bi bi-search"></i> No elements found matching "{{ elementSearchQuery }}"
                                </div>
                            </div>
                        </div>

                            <!-- Max elements message -->
                        <div v-else class="alert alert-info py-2 px-3 small mb-0">
                            <i class="bi bi-info-circle me-1"></i>
                            Maximum of 5 elements reached. Remove an element to add more.
                        </div>

                        <div class="border-top mt-3 pt-2">
                            <div class="d-flex align-items-center justify-content-between">
                                <div class="form-check form-switch">
                                    <input 
                                        class="form-check-input" 
                                        type="checkbox" 
                                        role="switch"
                                        :id="`${chart.id}-gridlines`"
                                        v-model="chart.editShowGrid">
                                    <label class="form-check-label" :for="`${chart.id}-gridlines`">
                                        <i class="bi bi-grid-3x2-gap text-muted me-1"></i>
                                        Show gridlines on chart
                                    </label>
                                </div>
                            </div>
                        </div>



                        </div>
                    </div>
                    

                    

                    

                  </div>
                  
                </div>
                
                <!-- Normal chart display -->
                <div v-else>
                  <div :id="`chart-${chart.id}`" class="chart-area"></div>
                  
                  <!-- Legend for multi-element charts -->
                  <div v-if="chart.elements.length > 1 " 
                    class="chart-legend mt-2" 
                    :class="`legend-${chart.legendPosition}`">
                    <div class="d-flex flex-wrap gap-2 justify-content-center">
                      <span v-for="(elementId, idx) in chart.elements" :key="elementId" 
                        class="badge" 
                        :style="{ backgroundColor: getElementColor(chart, idx) }">
                        {{ getElementLabel(elementId) }}
                      </span>
                    </div>
                  </div>
                  
                  <!-- Statistics Panel -->
                   <div v-if="chart.elementStats && hasVisibleStats(chart)" class="chart-stats mt-3 pt-3 border-top">
                        <div class="stats-container">
                             <!-- Compact view for small charts -->
                            <template v-if="isCompactChart(chart)">
                                <template v-for="elementId in chart.elements" :key="elementId">
                                  <div  v-if="shouldShowElementStats(chart, elementId)"
                                      class="element-stats-compact mb-2">
                                      <div class="d-flex align-items-center justify-content-between small">
                                          <div class="d-flex align-items-center">
                                              <div class="stats-color-indicator me-2" 
                                                  :style="{ backgroundColor: getElementColor(chart, chart.elements.indexOf(elementId)) }">
                                              </div>
                                              <strong>{{ getElementLabel(elementId) }}</strong>
                                          </div>
                                          <div class="text-muted">
                                              <span title="Average">Avg: {{ formatValue(chart.elementStats[elementId].avg, elementId) }}</span>
                                              <span class="mx-1">|</span>
                                              <span title="Range">{{ formatValue(chart.elementStats[elementId].min, elementId) }} - {{ formatValue(chart.elementStats[elementId].max, elementId) }}</span>
                                              <template v-if="enableElementSum(elementId)">
                                                <span class="mx-1">|</span>
                                                <span  title="Sum">Sum: {{ formatValue(chart.elementStats[elementId].sum, elementId) }}</span>
                                              </template>
                                            </div>
                                      </div>
                                  </div>
                                </template>
                            </template>
                            
                                    <!-- Full view for larger charts -->
                            <template v-else>
                                <template v-for="elementId in chart.elements" :key="elementId">
                                    <div v-if="shouldShowElementStats(chart, elementId)"
                                        class="element-stats mb-2">
                                        <div class="d-flex align-items-center mb-1">
                                            <div class="stats-color-indicator me-2" 
                                                :style="{ backgroundColor: getElementColor(chart, chart.elements.indexOf(elementId)) }">
                                            </div>
                                            <strong class="small">{{ getElementLabel(elementId) }}</strong>
                                        </div>
                                        <div class="row text-center small" v-if="chart.elementStats[elementId]">
                                            <div class="col-3">
                                                <div class="text-muted">Min</div>
                                                <strong class="text-nowrap">{{ formatValue(chart.elementStats[elementId].min, elementId) }}</strong>
                                            </div>
                                            <div class="col-3 ">
                                                <div class="text-muted">Max</div>
                                                <strong class="text-nowrap">{{ formatValue(chart.elementStats[elementId].max, elementId) }}</strong>
                                            </div>
                                            <div v-if="!enableElementSum(elementId)" class="col-3">
                                                <div class="text-muted">Avg</div>
                                                <strong class="text-nowrap">{{ formatValue(chart.elementStats[elementId].avg, elementId) }}</strong>
                                            </div>
                                            <div v-if="enableElementSum(elementId)" class="col-3">
                                                <div class="text-muted">Sum</div>
                                                <strong class="text-nowrap">{{ formatValue(chart.elementStats[elementId].sum, elementId) }}</strong>
                                            </div>
                                           
                                            <div  v-if="!enableElementSum(elementId)" class="col-3 ">
                                                <div class="text-muted">Median</div>
                                                <strong class="text-nowrap">{{ formatValue(chart.elementStats[elementId].median, elementId) }}</strong>
                                            </div>
                                            <!--
                                            <div v-if="chart.elements.length === 1" class="col-3 col-md-2">
                                                <div class="text-muted">Std Dev</div>
                                                <strong class="text-nowrap">{{ formatValue(chart.elementStats[elementId].stdDev, elementId) }}</strong>
                                            </div>
                                            -->
                                        </div>
                                    </div>
                                </template>
                            </template>
                        </div>
                    </div>
                </div>
              </div>
            </div>
          </div>
        </TransitionGroup>
      </div>
    
    </div>
     <FeedbackDialog 
      :show="showFeedbackDialog"
      title="Dashboard Feedback"
      question="Did the dashboard work for you?"
      placeholder="What worked well? What could be improved?"
      help-text="Your feedback helps us improve the dashboard experience."
      feature-name="WeatherDashboard"
      @close="showFeedbackDialog = false"
      @submit="handleFeedbackSubmit"
    />
  </div>
</template>

<script>
import { sanitizeText }  from '@/utils/sanitizeInput';
import FeedbackDialog from '@/components/FeedbackDialog.vue';
import { ELEMENT_META, resolveUnit } from '@/data/elementInfoMeta';
import * as d3 from 'd3';
import html2canvas from 'html2canvas';
export default {
  name: 'WeatherChartDashboard',
  components: {
    FeedbackDialog  // Register the component
  },
  props: {
    weatherData: {
      type: Object,
      required: true
    },
    unitGroup: {
      type: String,
      default: 'metric'
    },
    sessionId:String,
    sessionState:Object,
    packageDefn: Object, 
  },
  emits: ['package-updated'],
  data() {
    return {
        localPackageDefn: this.createLocalPackageDefn(),
        editModeEnabled: false,
      isGeneratingScreenshot: false,
      selectedPeriod: 'days',
      selectedElement: 'temp',
      selectedChartType: 'line',
      charts: [],
      chartIdCounter: 0,
      chartTitleEditIndex: null,
      //showLegend: false, 
      //enableZoom: false,
      enableTooltip: true,
      //showComparison: false,
      showAdvanced: false,
      tooltips: new Map(),
      resizeObserver: null,
      
      // Layout controls
      gridColumns: 3,
      layoutMode: 'custom', // 'grid', 'masonry', 'custom'
      enableDragDrop: true,
      //enableResize: false,
      
      // Drag and drop state
      draggedIndex: null,
      dragOverIndex: null,
      /*
      chartTypes: [
        { id: 'line', label: 'Line Chart', icon: 'bi-graph-up' },
        { id: 'bar', label: 'Bar Chart', icon: 'bi-bar-chart' },
        { id: 'area', label: 'Area Chart', icon: 'bi-graph-down' },
        { id: 'scatter', label: 'Scatter Plot', icon: 'bi-circle' }
      ],*/
      defaultElementColors: {
            // Temperature colors (warm tones)
            temp: '#ff6b6b',
            tempmax: '#ff4757',
            tempmin: '#4834d4',
            feelslike: '#ff9ff3',
            dew: '#686de0',
            
            // Precipitation colors (blue tones)
            precip: '#54a0ff',
            precipprob: '#48dbfb',
            precipcover: '#0abde3',
            snow: '#c8d6e5',
            snowdepth: '#8395a7',
            
            // Wind colors (green tones)
            windspeed: '#10ac84',
            windgust: '#00d2d3',
            winddir: '#1dd1a1',
            
            // Atmosphere colors
            pressure: '#ee5a24',
            humidity: '#5f27cd',
            cloudcover: '#576574',
            visibility: '#feca57',
            
            // Solar colors (yellow/orange)
            solarradiation: '#ff9f43',
            solarenergy: '#ffa502',
            uvindex: '#ff6348'
        },
        
        // Fallback colors for any element not in the map
        fallbackColors: [
            '#0d6efd', '#dc3545', '#28a745', '#ffc107', 
            '#6610f2', '#20c997', '#fd7e14', '#6c757d'
        ],
      elementChartTypes: {
        // Define which elements typically use which chart type
        precip: 'bar',
        snow: 'bar',
        precipprob: 'area',
        // Others default to line
      },
        
      // Define which elements should use right axis
      rightAxisElements: [
          'precip', 'precipprob', 'snow', 'snowdepth', 
          'humidity', 'cloudcover', 'precipcover'
      ],
      showElementDropdown: false,
      elementSearchQuery: '',
      showFeedbackDialog: false,
      
    };
  },
  
  computed: {
    queryDefinition() {
      return this.localPackageDefn && this.localPackageDefn.params;
    },

    dashboardDefinition() {
      // Get the dashboard definition from queryDefinition
      if (this.queryDefinition && this.queryDefinition.dashboarddefinition) {
        try {
          return JSON.parse(this.queryDefinition.dashboarddefinition);
        } catch (e) {
          console.error('Error parsing dashboard definition:', e);
          return this.getDefaultDashboardDefinition();
        }
      }
      return this.getDefaultDashboardDefinition();
    },

    availablePeriods() {
        // Force re-evaluation when weatherData changes
        const periods = [];
        if (this.weatherData?.days?.length) {
            periods.push('days');
            console.log('Days data available:', this.weatherData.days.length);
        }
        if (this.weatherData?.days?.[0]?.hours?.length) {
            periods.push('hours');
            console.log('Hours data available:', this.weatherData.days[0].hours.length);
        }
        console.log('Available periods:', periods);
        return periods;
    },
    
    elementGroups() {
        // Force re-evaluation when weatherData changes
        const dataTimestamp = this.weatherData?._timestamp || Date.now();
        
        const groups = {
            'Temperature': [
                { id: 'temp', label: 'Temperature' },
                { id: 'tempmax', label: 'Max Temperature' },
                { id: 'tempmin', label: 'Min Temperature' },
                { id: 'feelslike', label: 'Feels Like' },
                { id: 'dew', label: 'Dew Point' }
            ],
            'Precipitation': [
                { id: 'precip', label: 'Precipitation'},
                { id: 'precipprob', label: 'Precip Probability' },
                { id: 'precipcover', label: 'Precip Cover' },
                { id: 'snow', label: 'Snow' },
                { id: 'snowdepth', label: 'Snow Depth' }
            ],
            'Wind': [
                { id: 'windspeed', label: 'Wind Speed' },
                { id: 'windgust', label: 'Wind Gust' },
                { id: 'winddir', label: 'Wind Direction' }
            ],
            'Atmosphere': [
                { id: 'pressure', label: 'Pressure' },
                { id: 'humidity', label: 'Humidity' },
                { id: 'cloudcover', label: 'Cloud Cover' },
                { id: 'visibility', label: 'Visibility' }
            ],
            'Solar': [
                { id: 'solarradiation', label: 'Solar Radiation' },
                { id: 'solarenergy', label: 'Solar Energy' },
                { id: 'uvindex', label: 'UV Index' }
            ]
        };
        
        // Filter out elements that don't exist in the current data
        const filteredGroups = {};
        Object.keys(groups).forEach(groupName => {
            const filteredElements = groups[groupName].filter(element => {
                return this.hasElementInData(element.id);
            });
            if (filteredElements.length > 0) {
                filteredGroups[groupName] = filteredElements;
            }
        });
        
        console.log('Element groups updated', {
            timestamp: dataTimestamp,
            groupCount: Object.keys(filteredGroups).length
        });
        
        return filteredGroups;
    },
      
},
  
  watch: {
    // Add watcher for weatherData prop
    weatherData: {
        handler(newVal, oldVal) {
            // Only update if data actually changed
            if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
                console.log('Weather data changed, refreshing dashboard');
                
                // Re-evaluate available periods and element groups
                this.$nextTick(() => {
                    // Check if selected period is still valid
                    if (!this.availablePeriods.includes(this.selectedPeriod)) {
                        this.selectedPeriod = this.availablePeriods[0] || 'days';
                    }
                    
                    // Check if selected element is still valid
                    const allElements = Object.values(this.elementGroups).flat();
                    if (!allElements.find(e => e.id === this.selectedElement)) {
                        const firstGroup = Object.values(this.elementGroups)[0];
                        if (firstGroup && firstGroup.length > 0) {
                            this.selectedElement = firstGroup[0].id;
                        }
                    }
                    
                    // Refresh all existing charts with new data
                    this.refreshAllChartsWithNewData();
                });
            }
        },
        deep: true,
        immediate: false
    },

    packageDefn: {
        handler(newVal, oldVal) {
            // Only update if actually changed
            if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
                this.localPackageDefn = this.createLocalPackageDefn();
                this.loadDashboardDefinition();
            }
        },
        deep: true,
        immediate: false
    },

    
    enableTooltip(newValue) {
        this.updateDashboardSettings({ enableTooltip: newValue });
      this.refreshAllCharts();
    },
    'charts.length'(newValue) {
      this.$nextTick(() => {
        setTimeout(() => {
          this.refreshAllCharts();
        }, 100);
      });
    },
    gridColumns(newValue) {
        this.updateDashboardSettings({ gridColumns: newValue });
      this.$nextTick(() => {
        this.refreshAllCharts();
      });
    },
    layoutMode(newValue) {
        this.updateDashboardSettings({ layoutMode: newValue });
      if (this.layoutMode === 'custom') {
        this.charts.forEach(chart => {
          if (!chart.colSpan) chart.colSpan = 1;
          if (!chart.rowSpan) chart.rowSpan = 1;
        });
      }
      this.$nextTick(() => {
        this.refreshAllCharts();
      });
    }
  },
  
  methods: {
    getDateFormat(chartConfig, dateRange, chartWidth) {
        const startDate = dateRange[0];
        const endDate = dateRange[1];
        const rangeInDays = (endDate - startDate) / (1000 * 60 * 60 * 24);
        const isHourly = chartConfig.period === 'hours';
        
        // Calculate approximate number of ticks that will fit
        const tickWidth = 80; // Approximate width needed per tick
        const maxTicks = Math.floor(chartWidth / tickWidth);
        
        // For very small charts, use abbreviated formats
        if (chartWidth < 300) {
            if (isHourly && rangeInDays <= 1) {
                return '%H:%M'; // Just hours for single day
            } else if (rangeInDays <= 7) {
                return '%m/%d'; // Month/day for week view
            } else {
                return '%b %d'; // Abbreviated month + day
            }
        }
        
        // For hourly data
        if (isHourly) {
            if (rangeInDays <= 1) {
                // Single day: show hours
                return '%H:%M';
            } else if (rangeInDays <= 3) {
                // Few days: show day + hour
                return '%m/%d %H:%M';
            } else if (rangeInDays <= 7) {
                // Week: show day + abbreviated hour
                return '%m/%d %Ha';
            } else {
                // Longer: just show dates
                return '%m/%d/%Y';
            }
        }
        
        // For daily data
        if (rangeInDays <= 7) {
            // Week or less: show weekday + date
            return '%m/%d/%y'; //'%a %m/%d';
        } else if (rangeInDays <= 31) {
            // Month: show month/day
            return '%m/%d/%y'; //'%b %d';
        } else if (rangeInDays <= 365) {
            // Year: show month/day with year context
            const startYear = startDate.getFullYear();
            const endYear = endDate.getFullYear();
            if (startYear !== endYear) {
                return '%m/%d/%y'; // Include year if spanning years
            }
            return '%m/%d/%y'; //'%b %d';
        } else {
            // Multi-year: always show year
            return '%m/%d/%y'; //'%b %Y';
        }
    },
    
    getTickFormat(chartConfig, dateRange, chartWidth) {
        const format = this.getDateFormat(chartConfig, dateRange, chartWidth);
        return d3.timeFormat(format);
    },
    
    getTickInterval(chartConfig, dateRange, chartWidth) {
        const rangeInMs = dateRange[1] - dateRange[0];
        const rangeInDays = rangeInMs / (1000 * 60 * 60 * 24);
        const rangeInHours = rangeInMs / (1000 * 60 * 60);
        
        // Calculate ideal number of ticks based on width
        const tickWidth = 80;
        const idealTicks = Math.floor(chartWidth / tickWidth);
        
        if (chartConfig.period === 'hours') {
            if (rangeInHours <= 24) {
                // Every 3 hours for single day
                return d3.timeHour.every(3);
            } else if (rangeInHours <= 72) {
                // Every 6 hours for few days
                return d3.timeHour.every(6);
            } else if (rangeInDays <= 7) {
                // Daily for week
                return d3.timeDay.every(1);
            } else {
                // Adjust based on range
                const daysInterval = Math.ceil(rangeInDays / idealTicks);
                return d3.timeDay.every(daysInterval);
            }
        } else {
            // Daily data
            if (rangeInDays <= 7) {
                return d3.timeDay.every(1);
            } else if (rangeInDays <= 31) {
                const interval = Math.ceil(rangeInDays / idealTicks);
                return d3.timeDay.every(Math.max(1, interval));
            } else if (rangeInDays <= 365) {
                // Monthly intervals
                const monthsInterval = Math.ceil(rangeInDays / 30 / idealTicks);
                return d3.timeMonth.every(Math.max(1, monthsInterval));
            } else {
                // Yearly intervals
                return d3.timeYear.every(1);
            }
        }
    },
    isCompactChart(chart) {
        // Consider a chart compact if it's 1 column wide in a multi-column grid
        // or if we're on mobile
        const isMobile = window.innerWidth < 768;
        const isSmallChart = this.gridColumns > 2 && (!chart.colSpan || chart.colSpan === 1);
        return isMobile || isSmallChart;
    },
    showFeedback() {
        this.showFeedbackDialog = true;
      
    },
    
    handleFeedbackSubmit(feedbackData) {
      // Handle the feedback submission
      console.log('Feedback received:', feedbackData);
      
      // You can add any additional processing here
      // For example, show a success message
      if (window.VcCommonModals?.showAlert) {
        window.VcCommonModals.showAlert(
          'Thank You!',
          'Your feedback has been submitted successfully.',
          'Close'
        );
      }
      
      // Close the dialog (it also closes itself)
      this.showFeedbackDialog = false;
    },

    getDefaultElementColor(elementId) {
        return this.defaultElementColors[elementId] || 
               this.fallbackColors[0] || 
               '#0d6efd';
    },
    // Reset element color to default
    resetElementColor(chart, elementId) {
        chart.editElementConfigs[elementId].color = this.getDefaultElementColor(elementId);
    },
    getQuickAddElements() {
        // Return most commonly used elements for quick access
        const quickElements = ['temp', 'precip', 'windspeed', 'humidity', 'cloudcover'];
        return quickElements.filter(id => this.hasElementInData(id));
    },
    
    getFilteredElementGroups() {
        // Ensure elementGroups exists
        if (!this.elementGroups) {
            return {};
        }
        
        if (!this.elementSearchQuery) {
            return this.elementGroups;
        }
        
        const query = this.elementSearchQuery.toLowerCase();
        const filtered = {};
        
        Object.entries(this.elementGroups).forEach(([groupName, elements]) => {
            if (!elements) return; // Skip if elements is undefined
            
            const filteredElements = elements.filter(el => 
                el && (
                    el.label.toLowerCase().includes(query) || 
                    el.id.toLowerCase().includes(query)
                )
            );
            
            if (filteredElements.length > 0) {
                filtered[groupName] = filteredElements;
            }
        });
        
        return filtered;
    },

    hasFilteredElements() {
        const filtered = this.getFilteredElementGroups();
        return filtered && Object.keys(filtered).length > 0;
    },
    
    handleElementDropdownBlur() {
        // Delay hiding to allow click events to register
        setTimeout(() => {
            this.showElementDropdown = false;
            this.elementSearchQuery = '';
        }, 200);
    },
    
    getChartTitle(chart) {
        // Use custom title if set, otherwise generate from elements
        if (chart.title) {
        return chart.title;
        }
        
        // Auto-generate title from elements
        if (chart.elements.length === 1) {
            return this.getElementLabel(chart.elements[0]);
        } else if (chart.elements.length > 1) {
            return `${chart.elements.length} Elements`;
        }
        return 'Untitled Chart';
    },
    
    startEditTitle(index) {
        const chart = this.charts[index];
        
        // Store the original title in case user cancels
        chart.originalTitle = chart.title || '';
        
        // If no title exists, pre-populate with auto-generated title
        if (!chart.title) {
            chart.title = this.getChartTitle(chart);
        }
        
        this.chartTitleEditIndex = index;
        
        // Focus the input after Vue updates the DOM
        this.$nextTick(() => {
            const input = this.$refs.titleInput;
            if (input && input[0]) {
                input[0].focus();
                input[0].select();
            } else if (input) {
                input.focus();
                input.select();
            }
        });
    },
    
    cancelTitleEdit(index) {
        const chart = this.charts[index];
        
        // Restore original title
        if (chart.originalTitle !== undefined) {
            chart.title = chart.originalTitle;
            delete chart.originalTitle;
        }
        
        this.chartTitleEditIndex = null;
    },
    saveTitleEdit(index) {
        const chart = this.charts[index];
        
        if (chart.title) {
            chart.title = sanitizeText(chart.title, 100);
      
        }
        
        // Clean up the temporary original title
        delete chart.originalTitle;
        
        // Save to dashboard definition
        this.chartTitleEditIndex = null;
        this.saveDashboardDefinition();
    },
    refreshAllChartsWithNewData() {
        console.log('Refreshing all charts with new weather data');
        
        // Filter out charts that no longer have valid data
        const validCharts = [];
        
        this.charts.forEach(chart => {
            // Check if chart's elements are still valid with new data
            const validElements = chart.elements.filter(elementId => {
                const testData = this.getData(chart.period, elementId);
                return testData && testData.length > 0;
            });
            
            if (validElements.length > 0) {
                // Update chart with only valid elements
                if (validElements.length !== chart.elements.length) {
                    console.warn(`Chart ${chart.id}: Some elements no longer have data`, {
                        original: chart.elements,
                        valid: validElements
                    });
                    chart.elements = validElements;
                    chart.element = validElements[0];
                }
                validCharts.push(chart);
            } else {
                console.warn(`Chart ${chart.id}: No valid data, removing from dashboard`);
                // Clean up tooltip if it exists
                const tooltipKey = `tooltip-${chart.id}`;
                if (this.tooltips.has(tooltipKey)) {
                    this.tooltips.get(tooltipKey).remove();
                    this.tooltips.delete(tooltipKey);
                }
                // Clean up chart SVG
                d3.select(`#chart-${chart.id}`).selectAll('*').remove();
            }
        });
        
        // Update charts array if any were removed
        if (validCharts.length !== this.charts.length) {
            this.charts = validCharts;
            this.saveDashboardDefinition();
        }
        
        // Refresh all remaining charts
        this.$nextTick(() => {
            this.charts.forEach(chart => {
                // Clear and re-render each chart
                const container = d3.select(`#chart-${chart.id}`);
                container.selectAll('*').remove();
                
                // Re-render with new data
                this.renderChart(chart);
            });
        });
    },
    takeScreenshot() {
      const me = this;
      // Set loading state
      me.isGeneratingScreenshot = true;
      // Use $nextTick to ensure UI updates before starting heavy operation
          // Use $nextTick + requestIdleCallback for optimal scheduling
      me.$nextTick(() => {
        // Use requestIdleCallback to run during browser idle time
        if (window.requestIdleCallback) {
          window.requestIdleCallback(() => {
            me.performScreenshot();
          }, { timeout: 100 }); // Fallback timeout
        } else {
          // Fallback for browsers without requestIdleCallback
          setTimeout(() => {
            me.performScreenshot();
          }, 50);
        }
      });
      
    },
    performScreenshot() {
        const me = this;
        const contentDiv = document.getElementById('dashboardcontent');
        
        if (!contentDiv) {
            console.error('Dashboard content not found');
            me.isGeneratingScreenshot = false;
            return;
        }
        
        // Force layout recalculation before capturing
        contentDiv.style.overflow = 'visible';
        const rect = contentDiv.getBoundingClientRect();
        
        // Get actual scroll dimensions including all content
        const scrollHeight = contentDiv.scrollHeight;
        const scrollWidth = contentDiv.scrollWidth;
        
        // Add a small delay to ensure all charts are fully rendered
        setTimeout(() => {
            html2canvas(contentDiv, {
                useCORS: true,
                backgroundColor: '#ffffff',
                scale: 2, // Higher quality
                logging: false,
                width: scrollWidth,  // Use actual width
                height: scrollHeight, // Use actual height
                windowWidth: scrollWidth,
                windowHeight: scrollHeight,
                x: 0,  // Start from exact position
                y: 0,
                scrollX: 0,
                scrollY: -window.scrollY, // Account for page scroll
                ignoreElements: function(element) {
                    return element.classList.contains('noscreenshot');
                }
            }).then(function(canvas) {
            // Downscale for reasonable file size
                const scale = 1;
                const finalCanvas = document.createElement('canvas');
                const ctx = finalCanvas.getContext('2d');
                finalCanvas.width = canvas.width * scale;
                finalCanvas.height = canvas.height * scale;
                ctx.scale(scale, scale);
                ctx.drawImage(canvas, 0, 0);
                
                const imgData = finalCanvas.toDataURL('image/png');
                const link = document.createElement('a');
                link.href = imgData;
                link.download = `weather-dashboard-${new Date().toISOString().split('T')[0]}.png`;
                link.click();
                me.isGeneratingScreenshot = false;
            }).catch(function(error) {
                me.isGeneratingScreenshot = false;
                console.error('Error generating screenshot:', error);
            });
        }, 500); // Wait 500ms for charts to fully render
    },
     createLocalPackageDefn() {
      let newPackageDefn = this.packageDefn ? JSON.parse(JSON.stringify(this.packageDefn)) : {};
      
      // Initialize params if not exists
      if (!newPackageDefn.params) {
        newPackageDefn.params = {};
      }
      
      // Initialize dashboarddefinition if not exists
      if (!newPackageDefn.params.dashboarddefinition) {
        const defaultDef = this.getDefaultDashboardDefinition();
        newPackageDefn.params.dashboarddefinition = JSON.stringify(defaultDef);
      }
      
      return newPackageDefn;
    },
    getDefaultDashboardDefinition() {
        const defn = {
  "version": "1.0",
  "settings": {
    "gridColumns": 2,
    "layoutMode": "custom",
    "enableTooltip": true,
    "enableDragDrop": true
  },
  "charts": [
    {
      "id": "chart_1",
      "title": "Temperature",
      "elements": [
        "tempmax",
        "tempmin"
      ],
      "period": "days"
    },
    {
      "id": "chart_2",
      "title": "Precipitation",
      "elements": [
        "precip"
      ],
      "period": "days",
      "elementConfigs": {
        "precip": {
          "chartType": "bar",
          "axis": "right"
        }
      }
    },
    {
      "id": "chart_3",
      "title": "Wind",
      "elements": [
        "windspeed",
        "windgust"
      ],
      "period": "hours",
      "elementConfigs": {
        "windspeed": {
          "chartType": "bar"
        }
      }
    },
    {
      "id": "chart_4",
      "title": "Solar",
      "elements": [
        "solarradiation",
        "solarenergy"
      ],
      "period": "days",
      "elementConfigs": {
        "solarradiation": {
          "chartType": "area",
          "color": "#fbe86a"
        },
        "solarenergy": {
          "chartType": "bar",
          "axis": "right"
        }
      }
    }
  ],
  "exportDate": "2025-10-17T19:25:34.511Z",
  "metadata": {
    "chartsCount": 4,
    "location": "39.0035,-77.3674"
  }
};
        defn.lastUpdate = new Date().toISOString();
        return defn;
    },
    updateDashboardDefinition(newDefinition) {
      this.queryDefinition.dashboarddefinition = JSON.stringify(newDefinition)
    },
    loadDashboardDefinition() {
        // Load dashboard definition from queryDefinition
        const definition = this.dashboardDefinition;
        
        // Apply settings
        if (definition.settings) {
            this.gridColumns = definition.settings.gridColumns || 3;
            this.layoutMode = definition.settings.layoutMode || 'custom';
            this.enableTooltip = definition.settings.enableTooltip !== false;
            this.enableDragDrop = definition.settings.enableDragDrop !== false;
        }
        
        // Load charts
        if (definition.charts && Array.isArray(definition.charts)) {
            // Calculate counter FIRST
            const maxId = Math.max(0, ...definition.charts.map(c => {
                const match = c.id.match(/chart_(\d+)/);
                return match ? parseInt(match[1]) : 0;
            }));
            this.chartIdCounter = maxId + 1;
            
            // THEN set charts with all properties including title
            this.charts = definition.charts.map(chart => ({
                ...chart,
                title: chart.title || '', // Preserve title
                editing: false,
                showResizeControls: false,
                // Ensure arrays
                elements: chart.elements || [chart.element],
                // Ensure configs exist
                elementConfigs: chart.elementConfigs || {}
            }));
            
            // Finally render
            this.$nextTick(() => {
                this.charts.forEach(chart => {
                    this.renderChart(chart);
                });
            });
        }
    },
    
    saveDashboardDefinition() {
        // Create a clean version of charts without temporary UI state
        const cleanCharts = this.charts.map(chart => {
            // Create a clean copy
            const cleanChart = {
                id: chart.id,
                elements: chart.elements || [chart.element],
                period: chart.period,
                title: chart.title, // INCLUDE TITLE
                showGrid: chart.showGrid !== false,
                elementConfigs: chart.elementConfigs || {},
            };
            
            // Only include layout properties if in custom mode
            if (this.layoutMode === 'custom') {
                cleanChart.colSpan = chart.colSpan || 1;
                cleanChart.rowSpan = chart.rowSpan || 1;
            }
            
            // Remove any undefined or default values to keep export clean
            Object.keys(cleanChart).forEach(key => {
                if (cleanChart[key] === undefined || cleanChart[key] === null) {
                    delete cleanChart[key];
                }
            });
            
            // Clean up elementConfigs - remove defaults
            if (cleanChart.elementConfigs) {
                Object.keys(cleanChart.elementConfigs).forEach(elementId => {
                    const config = cleanChart.elementConfigs[elementId];
                    // Remove default values to keep export compact
                    if (config.showStats === true) delete config.showStats;
                    if (config.axis === 'left') delete config.axis;
                    if (config.chartType === 'line') delete config.chartType;
                    
                    // If config is empty after cleanup, remove it
                    if (Object.keys(config).length === 0) {
                        delete cleanChart.elementConfigs[elementId];
                    }
                });
                
                // If elementConfigs is empty, remove it
                if (Object.keys(cleanChart.elementConfigs).length === 0) {
                    delete cleanChart.elementConfigs;
                }
            }
            
            return cleanChart;
        });
        
        const definition = {
            version: '1.0',
            settings: {
                gridColumns: this.gridColumns,
                layoutMode: this.layoutMode,
                enableTooltip: this.enableTooltip,
                enableDragDrop: this.enableDragDrop
            },
            charts: cleanCharts,
            lastUpdate: new Date().toISOString()
        };
        
        // Update the queryDefinition
        if (this.queryDefinition) {
            this.queryDefinition.dashboarddefinition = JSON.stringify(definition);
            
            // Emit event to notify parent component
            this.$emit('package-updated', this.localPackageDefn);
        }
    },

     updateDashboardSettings(settings) {
      // Update specific settings and save
      const definition = this.dashboardDefinition;
      definition.settings = { ...definition.settings, ...settings };
      definition.lastUpdate = new Date().toISOString();
      
      if (this.queryDefinition) {
        this.queryDefinition.dashboarddefinition = JSON.stringify(definition);
        this.$emit('package-updated', this.localPackageDefn);
      }
    },

    getChartIcon(chart) {
      if (chart.elements.length > 1) {
        return 'bi bi-layers';
      }
      return this.getElementIcon(chart.elements[0] || chart.element);
    },
    
    startEditChart(index) {
        const chart = this.charts[index];
        
        chart.editing = true;
        chart.editTitle = chart.title || ''; 
        chart.editElements = [...(chart.elements || [chart.element])];
        chart.editPeriod = chart.period;
        chart.editShowGrid = chart.showGrid !== false; 
        
        // Initialize element configurations with colors
        chart.editElementConfigs = {};
        chart.editElements.forEach(elementId => {
            chart.editElementConfigs[elementId] = {
                chartType: chart.elementConfigs?.[elementId]?.chartType || 
                          this.elementChartTypes[elementId] || 'line',
                axis: chart.elementConfigs?.[elementId]?.axis || 
                     (this.rightAxisElements.includes(elementId) ? 'right' : 'left'),
                showStats: chart.elementConfigs?.[elementId]?.showStats !== false,
                color: chart.elementConfigs?.[elementId]?.color || 
                       this.getDefaultElementColor(elementId)
            };
        });
    },
        // Update addElementToChart to close dropdown
    addElementToChart(chart, elementId) {
        if (chart.editElements.length >= 5) return;
        
        chart.editElements.push(elementId);
        chart.editElementConfigs[elementId] = {
            chartType: this.elementChartTypes[elementId] || 'line',
            axis: this.rightAxisElements.includes(elementId) ? 'right' : 'left',
            showStats: true,
            color: this.getDefaultElementColor(elementId)
        };
        
        // Close dropdown and clear search
        this.showElementDropdown = false;
        this.elementSearchQuery = '';
    },
    
    removeElementFromChart(chart, elementId) {
        const index = chart.editElements.indexOf(elementId);
        if (index > -1) {
        chart.editElements.splice(index, 1);
        delete chart.editElementConfigs[elementId];
        }
    },
    // Save chart edits
    saveChartEdits(index) {
        const chart = this.charts[index];
        
        if (chart.editElements.length === 0) {
            alert('Please select at least one element');
            return;
        }
        
        // Apply changes
        chart.elements = [...chart.editElements];
        chart.element = chart.elements[0];
        chart.period = chart.editPeriod;
        chart.showGrid = chart.editShowGrid; // Save grid setting
        chart.elementConfigs = { ...chart.editElementConfigs };
        
        // Clear edit mode
        chart.editing = false;
        chart.isNew = false;
        delete chart.editElements;
        delete chart.editPeriod;
        delete chart.editElementConfigs;
        delete chart.editShowGrid; // Clean up

        // Re-render the chart
        this.$nextTick(() => {
            this.renderChart(chart);
            this.saveDashboardDefinition();
        });
    },
    

    
    // Cancel chart edit
     // Updated cancel to handle new charts
    cancelChartEdit(index) {
      const chart = this.charts[index];
      
      // If it's a new chart that was cancelled, remove it entirely
      if (chart.isNew) {
        this.charts.splice(index, 1);
        return;
      }
      
      // For existing charts, just exit edit mode
      chart.editing = false;
      delete chart.editElements;
      delete chart.editPeriod;
      delete chart.editColorScheme;
      

      // IMPORTANT: Re-render the chart to restore its display
        this.$nextTick(() => {
            this.renderChart(chart);
        });
    },
    
    // Get color for element in multi-element chart
    getElementColor(chart, index) {
        const elementId = chart.elements[index];
        
        // First check if there's a custom color configured
        if (chart.elementConfigs?.[elementId]?.color) {
            return chart.elementConfigs[elementId].color;
        }
        
        // Then check default colors
        if (this.defaultElementColors[elementId]) {
            return this.defaultElementColors[elementId];
        }
        
        // Finally use fallback colors based on index
        return this.fallbackColors[index % this.fallbackColors.length];
    },
    hasElementInData(elementId) {
      const sampleData = this.weatherData?.days?.[0];
      return sampleData && elementId in sampleData;
    },
    
    getElementLabel(elementId) {
      for (const group of Object.values(this.elementGroups)) {
        const element = group.find(e => e.id === elementId);
        if (element) return element.label;
      }
      return elementId;
    },
    enableElementSum(elementId) {
      // Elements that support sum
      const sumElements = ['precip', 'snow'];
      return sumElements.includes(elementId);
    },
    getElementIcon(elementId) {
      const iconMap = {
        temp: 'bi bi-thermometer-half',
        precip: 'bi bi-cloud-rain',
        wind: 'bi bi-wind',
        pressure: 'bi bi-speedometer2',
        humidity: 'bi bi-droplet',
        cloud: 'bi bi-clouds',
        solar: 'bi bi-sun',
        uv: 'bi bi-brightness-high',
        snow: 'bi bi-snow',
        visibility: 'bi bi-eye'
      };
      
      for (const [prefix, icon] of Object.entries(iconMap)) {
        if (elementId.toLowerCase().includes(prefix)) return icon;
      }
      return 'bi bi-graph-up';
    },
    
    formatValue(value, elementId) {
      if (value == null || isNaN(value)) return '-';
      /*
      const unitMap = {
        temp: '°',
        tempmax: '°',
        tempmin: '°',
        feelslike: '°',
        dew: '°',
        precip: 'mm',
        precipprob: '%',
        precipcover: '%',
        snow: 'cm',
        snowdepth: 'cm',
        windspeed: 'km/h',
        windgust: 'km/h',
        winddir: '°',
        pressure: 'mb',
        humidity: '%',
        cloudcover: '%',
        visibility: 'km',
        solarradiation: 'W/m²',
        solarenergy: 'MJ/m²',
        uvindex: ''
      };
      
      const unit = unitMap[elementId] || '';
     
        */
      const m = ELEMENT_META[elementId] || {};
      const unit = resolveUnit(m?.unit, this.unitGroup);

      const precision = elementId.includes('precip')  ? (this.unitGroup==="us" ? 2 : 1) : 0;
      return `${value.toFixed(precision)}${unit}`;
    },
    
    // New layout methods
    getChartClass(chart) {
      if (this.layoutMode === 'custom' && chart.colSpan) {
        const colClass = `col-12 col-md-${Math.min(12, chart.colSpan * (12 / this.gridColumns))}`;
        return colClass;
      }
      
      // Default responsive classes based on grid columns
      switch (this.gridColumns) {
        case 1: return 'col-12';
        case 2: return 'col-12 col-md-6';
        case 3: return 'col-12 col-md-6 col-lg-4';
        case 4: return 'col-12 col-md-6 col-lg-3';
        case 6: return 'col-12 col-md-4 col-lg-2';
        default: return 'col-12 col-md-6 col-lg-4';
      }
    },
    
    getChartStyle(chart) {
      if (this.layoutMode === 'custom' && chart.rowSpan > 1) {
        return {
          'grid-row': `span ${chart.rowSpan}`
        };
      }
      return {};
    },
    
    getChartBodyStyle(chart) {
      if (this.layoutMode === 'custom' && chart.rowSpan > 1) {
        return {
          'min-height': `${chart.rowSpan * 300}px`
        };
      }
      return {};
    },
    
    // Drag and drop methods
    handleDragStart(e, index) {
      this.draggedIndex = index;
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/html', ''); // Firefox requires this
    },
    
    handleDragOver(e, index) {
      if (this.draggedIndex !== null) {
        e.dataTransfer.dropEffect = 'move';
        this.dragOverIndex = index;
      }
    },
    
    // Update handleDrop with logging (around line 1880)
    handleDrop(e, dropIndex) {
      console.log(`=== DRAG OPERATION ===`);
      console.log(`Dragging from index: ${this.draggedIndex}`);
      console.log(`Dropping at index: ${dropIndex}`);
      console.log(`Current charts order:`, this.charts.map(c => c.id));
      
      if (this.draggedIndex !== null && this.draggedIndex !== dropIndex) {
        const draggedChart = this.charts[this.draggedIndex];
        console.log(`Dragged chart ID: ${draggedChart.id}`);
        
        // Remove from old position
        this.charts.splice(this.draggedIndex, 1);
        console.log(`After removal:`, this.charts.map(c => c.id));
        
        
        console.log(`Calculated insert index: ${dropIndex}`);
        
        // Insert at new position
        this.charts.splice(dropIndex, 0, draggedChart);
        console.log(`After insertion:`, this.charts.map(c => c.id));
        
        // Save to dashboard definition after reordering
        this.saveDashboardDefinition();

        // Refresh charts after reordering
        this.$nextTick(() => {
          this.refreshAllCharts();
        });
      }
      
      this.draggedIndex = null;
      this.dragOverIndex = null;
    },
    
    handleDragEnd() {
      this.draggedIndex = null;
      this.dragOverIndex = null;
    },
    
    // Manual move methods (when drag is disabled)
    moveChart(index, direction) {
      const newIndex = index + direction;
      if (newIndex >= 0 && newIndex < this.charts.length) {
        const chart = this.charts[index];
        this.charts.splice(index, 1);
        this.charts.splice(newIndex, 0, chart);

        // Save to dashboard definition
        this.saveDashboardDefinition();
        
        this.$nextTick(() => {
          this.refreshAllCharts();
        });
      }
    },
    // Toggle resize controls for a specific chart
    toggleResizeControls(index) {
      const chart = this.charts[index];
      
      // Toggle the resize controls for this chart
      chart.showResizeControls = !chart.showResizeControls;
      
      // Hide resize controls for all other charts to avoid clutter
      this.charts.forEach((c, i) => {
        if (i !== index) {
          c.showResizeControls = false;
        }
      });
    },
    // Resize methods
    resizeChart(index, dimension, delta) {
      const chart = this.charts[index];
      
      if (!chart.colSpan) chart.colSpan = 1;
      if (!chart.rowSpan) chart.rowSpan = 1;
      
      if (dimension === 'width') {
        const newSpan = chart.colSpan + delta;
        if (newSpan >= 1 && newSpan <= this.gridColumns) {
          chart.colSpan = newSpan;
        }
      } else if (dimension === 'height') {
        const newSpan = chart.rowSpan + delta;
        if (newSpan >= 1 && newSpan <= 3) {
          chart.rowSpan = newSpan;
        }
      }

     // Save to dashboard definition
      this.saveDashboardDefinition();
      this.$nextTick(() => {
        this.renderChart(chart);
      });
    },
    
    // Duplicate chart
     duplicateChart(index) {
      const originalChart = this.charts[index];
      const newChart = {
        ...originalChart,
        id: `chart_${this.chartIdCounter++}`,
        colSpan: originalChart.colSpan || 1,
        rowSpan: originalChart.rowSpan || 1,
        showResizeControls: false // Start with resize controls hidden
      };
      
      this.charts.splice(index + 1, 0, newChart);
        // Save to dashboard definition
      this.saveDashboardDefinition();
      
      this.$nextTick(() => {
        this.renderChart(newChart);
      });
    },
    addNewChart() {
        const chartId = `chart_${this.chartIdCounter++}`;
        
        const firstGroup = Object.values(this.elementGroups)[0];
        const defaultElement = firstGroup && firstGroup[0] ? firstGroup[0].id : null;
        
        const newChart = {
                id: chartId,
                element: null,
                elements: [],
                period: this.availablePeriods[0] || 'days',
                elementStats: {},
                colSpan: this.layoutMode === 'custom' ? 1 : undefined,
                rowSpan: this.layoutMode === 'custom' ? 1 : undefined,
                showGrid: true, // Default to showing grid
                editing: true,
                isNew: true,
                editElements: defaultElement ? [defaultElement] : [],
                editPeriod: this.availablePeriods[0] || 'days',
                editShowGrid: true, // For edit mode - default to true
                editElementConfigs: {},
                showResizeControls: false,
            
        };
        
        if (defaultElement) {
            newChart.editElementConfigs[defaultElement] = {
                chartType: this.elementChartTypes[defaultElement] || 'line',
                axis: this.rightAxisElements.includes(defaultElement) ? 'right' : 'left',
                showStats: true,
                color: this.getDefaultElementColor(defaultElement)
            };
        }
        
        this.charts.push(newChart);
    },
    // Update existing methods
    // Modified addChart to support multiple elements
     addChart() {
      if (!this.selectedElement || !this.selectedPeriod) {
        console.warn('No element or period selected');
        return;
      }
      
      const testData = this.getData(this.selectedPeriod, this.selectedElement);
      if (!testData || testData.length === 0) {
        alert(`No data available for ${this.getElementLabel(this.selectedElement)} in ${this.selectedPeriod}`);
        return;
      }
      
      const chartId = `chart_${this.chartIdCounter++}`;
      const chartConfig = {
        id: chartId,
        element: this.selectedElement,
        elements: [this.selectedElement],
        period: this.selectedPeriod,
        type: this.selectedChartType,
        stats: null,
        colSpan: this.layoutMode === 'custom' ? 1 : undefined,
        rowSpan: this.layoutMode === 'custom' ? 1 : undefined,
        colorScheme: 'default',
       
 
        editing: false,
        showResizeControls: false // Add this new property
      };
      
      this.charts.push(chartConfig);
      // Save to dashboard definition
      this.saveDashboardDefinition();
    },
    removeChart(index) {
      const chart = this.charts[index];
      
      // Clean up tooltip if it exists
      const tooltipKey = `tooltip-${chart.id}`;
      if (this.tooltips.has(tooltipKey)) {
        this.tooltips.get(tooltipKey).remove();
        this.tooltips.delete(tooltipKey);
      }
      
      // Clean up chart
      d3.select(`#chart-${chart.id}`).selectAll('*').remove();
      
      // Remove from array
      this.charts.splice(index, 1);
      
        // Save to dashboard definition
      this.saveDashboardDefinition();
    },
    
    refreshAllCharts() {
      // Add a small debounce to prevent multiple rapid refreshes
      if (this.refreshTimeout) {
        clearTimeout(this.refreshTimeout);
      }
      
      this.refreshTimeout = setTimeout(() => {
        this.charts.forEach(chart => {
          // Force recalculation of dimensions
          this.renderChart(chart);
        });
      }, 50);
    },
     // Update clearDashboard with better confirmation
    clearDashboard() {
      if (!window.VcCommonModals) {
        // Fallback to native confirm
        if (confirm('Are you sure you want to clear all charts?')) {
          this.performClearDashboard();
        }
      } else {
        window.VcCommonModals.showConfirm(
          "Clear Dashboard", 
          "Are you sure you want to remove all charts? This action cannot be undone."
        )
        .then(() => {
          this.performClearDashboard();
        })
        .catch(() => {
          console.log("User cancelled clear dashboard");
        });
      }
    },
    
    performClearDashboard() {
      // Clean up tooltips
      this.tooltips.forEach(tooltip => tooltip.remove());
      this.tooltips.clear();
      
      // Clear charts
      this.charts = [];
      
      // Save to dashboard definition
      this.saveDashboardDefinition();
    },
    
    updateGridLayout() {
      this.$nextTick(() => {
        this.refreshAllCharts();
      });
    },
    
    getData(period, elementId) {
        const data = [];
        
        try {
            if (period === 'days') {
            this.weatherData.days?.forEach(day => {
                if (elementId in day && day[elementId] != null) {
                // Parse the date as local midnight, not UTC
                // This ensures the date displays at the correct day boundary
                const [year, month, date] = day.datetime.split('-').map(Number);
                const localDate = new Date(year, month - 1, date); // month is 0-indexed
                
                data.push({
                    date: localDate,
                    value: parseFloat(day[elementId]),
                    label: day.datetime,
                    displayLabel: day.datetime
                });
                }
            });
            } else if (period === 'hours') {
            let lastDate = null;
            this.weatherData.days?.forEach(day => {
                day.hours?.forEach((hour, hourIndex) => {
                if (elementId in hour && hour[elementId] != null) {
                    // Parse date and time components separately to avoid timezone issues
                    const [year, month, date] = day.datetime.split('-').map(Number);
                    const [hours, minutes, seconds] = hour.datetime.split(':').map(Number);
                    
                    // Create date in local timezone
                    const localDateTime = new Date(year, month - 1, date, hours, minutes, seconds || 0);
                    
                    const showDate = hour.datetime === '00:00:00' || 
                                    (lastDate !== day.datetime && hourIndex === 0);
                    lastDate = day.datetime;
                    
                    data.push({
                    date: localDateTime,
                    value: parseFloat(hour[elementId]),
                    label: hour.datetime,
                    displayLabel: showDate ? `${day.datetime} ${hour.datetime.substring(0, 5)}` : hour.datetime.substring(0, 5),
                    showDate: showDate,
                    dayDate: day.datetime
                    });
                }
                });
            });
            }
        } catch (error) {
            console.error('Error getting data:', error);
        }
        
        return data;
        },
    calculateAllElementStats(allData, chartConfig) {
        const elementStats = {};
        
        chartConfig.elements.forEach(elementId => {
            if (allData[elementId]) {
                elementStats[elementId] = this.calculateStats(allData[elementId]);
            }
        });
        
        return elementStats;
    },
    calculateStats(data) {
        if (!data || !data.length) return null;
        
        const values = data.map(d => d.value).filter(v => v != null && !isNaN(v));
        if (values.length === 0) return null;
        
        const min = d3.min(values);
        const max = d3.max(values);
        const avg = d3.mean(values);
        const sum = d3.sum(values);
        const median = d3.median(values);
        const stdDev = d3.deviation(values);
        
        return { min, max, avg, sum, median, stdDev };
    },
    renderMixedChart(containerId, allData, chartConfig) {
        const container = d3.select(`#chart-${containerId}`);
        const containerNode = container.node();
        if (!containerNode) return;
        
        const rect = containerNode.getBoundingClientRect();
        if (rect.width === 0 || rect.height === 0) {
        setTimeout(() => this.renderMixedChart(containerId, allData, chartConfig), 100);
        return;
        }
        
        container.selectAll('*').remove();
        const margin = { top: 20, right: 60, bottom: 60, left: 60 };
        const width = Math.max(rect.width - margin.left - margin.right, 200);
        const height = 300 - margin.top - margin.bottom;
        
        const svg = container.append('svg')
        .attr('viewBox', `0 0 ${width + margin.left + margin.right} ${height + margin.top + margin.bottom}`)
        .attr('preserveAspectRatio', 'xMidYMid meet')
        .style('width', '100%')
        .style('height', '100%')
        .style('max-height', '400px');
        
        const g = svg.append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);
        

        
        // Separate elements by axis
        const leftAxisElements = [];
        const rightAxisElements = [];
        
        chartConfig.elements.forEach(elementId => {
        const config = chartConfig.elementConfigs?.[elementId] || {};
        if (config.axis === 'right') {
            rightAxisElements.push(elementId);
        } else {
            leftAxisElements.push(elementId);
        }
        });
        
        // X scale (shared)
        const allDates = [];
        Object.values(allData).forEach(data => {
        data.forEach(d => allDates.push(d.date));
        });
        
        const dateExtent = d3.extent(allDates);
        const x = d3.scaleTime()
            .domain(dateExtent)
            .range([0, width]);
        
        // Left Y scale
        let yLeft = null;
        if (leftAxisElements.length > 0) {
        const leftValues = [];
        leftAxisElements.forEach(elementId => {
            if (allData[elementId]) {
            allData[elementId].forEach(d => leftValues.push(d.value));
            }
        });
        
        yLeft = d3.scaleLinear()
            .domain(d3.extent(leftValues))
            .nice()
            .range([height, 0]);
        }
        
        // Right Y scale
        let yRight = null;
        if (rightAxisElements.length > 0) {
        const rightValues = [];
        rightAxisElements.forEach(elementId => {
            if (allData[elementId]) {
            allData[elementId].forEach(d => rightValues.push(d.value));
            }
        });
        
        yRight = d3.scaleLinear()
            .domain(d3.extent(rightValues))
            .nice()
            .range([height, 0]);
        }
        
        // Add gridlines
        const showGrid = chartConfig.showGrid !== false; // Default to true if not set
        if (showGrid) {
            const gridScale = yLeft || yRight;
            if (gridScale) {
                g.append('g')
                    .attr('class', 'grid grid-y')
                    .call(d3.axisLeft(gridScale)
                        .tickSize(-width)
                        .tickFormat(''))
                    .selectAll('line')
                    .style('stroke-dasharray', '2,2')
                    .style('stroke', '#101010')
                    .style('opacity', 0.3);
                
                g.selectAll('.grid path').style('stroke-width', 0);
            }
        }
        
        // Add axes

        // Add X axis with dynamic formatting
        const tickFormat = this.getTickFormat(chartConfig, dateExtent, width);
        const tickInterval = this.getTickInterval(chartConfig, dateExtent, width);
        
        const xAxis = d3.axisBottom(x)
            .tickFormat(tickFormat);
        
        // Apply tick interval if specified
        if (tickInterval) {
            xAxis.ticks(tickInterval);
        } else {
            // Let D3 choose optimal number of ticks
            const maxTicks = Math.floor(width / 80);
            xAxis.ticks(Math.min(maxTicks, 10));
        }
        g.append('g')
            .attr('class', 'x-axis')
            .attr('transform', `translate(0,${height})`)
            .call(xAxis)
            .selectAll('text')
            .style('text-anchor', 'end')
            .attr('dx', '-.8em')
            .attr('dy', '.15em')
            .attr('transform', 'rotate(-45)');
    
        // Add a secondary axis line for context if needed
        /*
        if (chartConfig.period === 'hours' && dateExtent[1] - dateExtent[0] > 86400000) {
            // Add day markers for hourly charts spanning multiple days
            const dayAxis = d3.axisBottom(x)
                .ticks(d3.timeDay.every(1))
                .tickFormat(d3.timeFormat('%b %d, %Y'))
                .tickSize(0);
            
            g.append('g')
                .attr('class', 'x-axis-context')
                .attr('transform', `translate(0,${height + 20})`)
                .call(dayAxis)
                .selectAll('text')
                .style('font-size', '10px')
                .style('opacity', 0.7);
        }
        */
        if (yLeft) {
        g.append('g')
            .attr('class', 'y-axis-left')
            .call(d3.axisLeft(yLeft).ticks(5))
            .append('text')
            .attr('transform', 'rotate(-90)')
            .attr('y', -40)
            .attr('x', -height / 2)
            .attr('fill', '#000')
            .style('text-anchor', 'middle')
            .style('font-size', '12px')
            .text(leftAxisElements.map(id => this.getElementLabel(id)).join(', '));
        }
        
        if (yRight) {
        g.append('g')
            .attr('class', 'y-axis-right')
            .attr('transform', `translate(${width}, 0)`)
            .call(d3.axisRight(yRight).ticks(5))
            .append('text')
            .attr('transform', 'rotate(90)')
            .attr('y', -40)
            .attr('x', height / 2)
            .attr('fill', '#000')
            .style('text-anchor', 'middle')
            .style('font-size', '12px')
            .text(rightAxisElements.map(id => this.getElementLabel(id)).join(', '));
        }
        
        // Render each element based on its chart type
        let colorIndex = 0;
        chartConfig.elements.forEach(elementId => {
        const data = allData[elementId];
        if (!data) return;
        
        const config = chartConfig.elementConfigs?.[elementId] || {};
        // Use element-specific chart type if configured, otherwise use the base chart type
        const chartType = config.chartType || 'line'; 
        const axis = config.axis || 'left';
        const y = axis === 'right' ? yRight : yLeft;
        
        // If no y scale exists for this axis, use left as fallback
        const actualY = y || yLeft;
        if (!actualY) {
            console.warn(`No Y scale for element ${elementId}`);
            return;
        }
        
         const color = config.color || 
                      this.defaultElementColors[elementId] || 
                      this.fallbackColors[colorIndex % this.fallbackColors.length];
    
        
        switch (chartType) {
            case 'bar':
                // First, identify ALL bar elements in this chart for proper grouping
                const allBarElements = chartConfig.elements.filter(id => {
                    const config = chartConfig.elementConfigs?.[id] || {};
                    return (config.chartType || 'line') === 'bar';
                });
                
                // Get this element's position among bar elements
                const barElementIndex = allBarElements.indexOf(elementId);
                const totalBarElements = allBarElements.length;
                
                // Calculate bar dimensions
                const barData = data;
                const maxGroupWidth = Math.min(width / barData.length, 60); // Max 60px total group width
                const barPadding = 0.2; // 20% padding between groups
                const groupWidth = maxGroupWidth * (1 - barPadding);
                const individualBarWidth = groupWidth / totalBarElements;
                
                // Calculate offset for this specific bar within the group
                const barOffset = (barElementIndex - (totalBarElements - 1) / 2) * individualBarWidth;
                
                // Create clipping path
                const clipId = `clip-bars-${containerId}-${elementId}`;
                if (!g.select(`#${clipId}`).node()) {
                    g.append('defs').append('clipPath')
                        .attr('id', clipId)
                        .append('rect')
                        .attr('x', 0)
                        .attr('y', 0)
                        .attr('width', width)
                        .attr('height', height);
                }
                
                // Create bar group with clipping
                const barGroup = g.append('g')
                    .attr('class', `bars-group-${elementId}`)
                    .attr('clip-path', `url(#${clipId})`);
                
                barGroup.selectAll(`.bar-${elementId}`)
                    .data(barData)
                    .enter().append('rect')
                    .attr('class', `bar bar-${elementId}`)
                    .attr('x', d => {
                        const centerX = x(d.date);
                        // Position bar within the group
                        return centerX + barOffset - (individualBarWidth / 2);
                    })
                    .attr('y', d => {
                        const yValue = actualY(d.value);
                        return d.value >= 0 ? yValue : actualY(0);
                    })
                    .attr('width', individualBarWidth * 0.9) // 90% of allocated width for visual separation
                    .attr('height', d => {
                        const yZero = actualY(0) || height;
                        const yValue = actualY(d.value);
                        return Math.abs(yValue - yZero);
                    })
                    .attr('fill', color)
                    .attr('opacity', 0.8)
                    .attr('stroke', color)
                    .attr('stroke-width', 0.5)
                    .on('mouseover', function() {
                        d3.select(this)
                            .transition()
                            .duration(100)
                            .attr('opacity', 1);
                    })
                    .on('mouseout', function() {
                        d3.select(this)
                            .transition()
                            .duration(100)
                            .attr('opacity', 0.8);
                    });
                break;
            case 'area':
                const areaData = data;
                
                // Create area generator
                const area = d3.area()
                    .x(d => x(d.date))
                    .y0(height)
                    .y1(d => actualY(d.value))
                    .curve(d3.curveMonotoneX);
                
                // Create line generator for the top of the area
                const areaLine = d3.line()
                    .x(d => x(d.date))
                    .y(d => actualY(d.value))
                    .curve(d3.curveMonotoneX);
                
                // Add gradient for this area
                const gradientId = `area-gradient-${containerId}-${elementId}`;
                const gradient = svg.append('defs')
                    .append('linearGradient')
                    .attr('id', gradientId)
                    .attr('gradientUnits', 'userSpaceOnUse')
                    .attr('x1', 0).attr('y1', height)
                    .attr('x2', 0).attr('y2', 0);
                
                gradient.append('stop')
                    .attr('offset', '0%')
                    .attr('stop-color', color)
                    .attr('stop-opacity', 0.1);
                
                gradient.append('stop')
                    .attr('offset', '100%')
                    .attr('stop-color', color)
                    .attr('stop-opacity', 0.6);
                
                // Add the area
                g.append('path')
                    .datum(areaData)
                    .attr('class', `area area-${elementId}`)
                    .attr('fill', `url(#${gradientId})`)
                    .attr('d', area);
                
                // Add the line on top of the area
                g.append('path')
                    .datum(areaData)
                    .attr('class', `area-line line-${elementId}`)
                    .attr('fill', 'none')
                    .attr('stroke', color)
                    .attr('stroke-width', 2)
                    .attr('d', areaLine);
                
                // Add dots for interaction
                g.selectAll(`.area-dot-${elementId}`)
                    .data(areaData)
                    .enter().append('circle')
                    .attr('class', `dot area-dot-${elementId}`)
                    .attr('cx', d => x(d.date))
                    .attr('cy', d => actualY(d.value))
                    .attr('r', 2)
                    .attr('fill', color)
                    .style('cursor', 'pointer');
                break;
            case 'scatter':
                // Add scatter plot support
                g.selectAll(`.dot-${elementId}`)
                .data(data)
                .enter().append('circle')
                .attr('class', `dot dot-${elementId}`)
                .attr('cx', d => x(d.date))
                .attr('cy', d => actualY(d.value))
                .attr('r', 4)
                .attr('fill', color)
                .attr('opacity', 0.7);
                break;
            case 'line':
            default:
                const line = d3.line()
                    .x(d => x(d.date))
                    .y(d => y(d.value))
                    .curve(d3.curveMonotoneX);
                
                g.append('path')
                    .datum(data)
                    .attr('class', `line line-${elementId}`)
                    .attr('fill', 'none')
                    .attr('stroke', color)
                    .attr('stroke-width', 2)
                    .attr('d', line);
                
                // Add dots for lines
                g.selectAll(`.dot-${elementId}`)
                    .data(data)
                    .enter().append('circle')
                    .attr('class', `dot dot-${elementId}`)
                    .attr('cx', d => x(d.date))
                    .attr('cy', d => y(d.value))
                    .attr('r', 2)
                    .attr('fill', color);
                break;
            }
        
            colorIndex++;
        });
        
        // Add tooltips
        if (this.enableTooltip) {
            this.addMixedChartTooltips(containerId, g, x, yLeft, yRight, 
                                   allData, chartConfig, width, height);
        }

        
    },
    // Add this method after renderMixedChart
    addMixedChartTooltips(containerId, g, x, yLeft, yRight, allData, chartConfig, colors, width, height) {
        if (!this.enableTooltip) return;
        
        const tooltip = d3.select('body').append('div')
            .attr('class', 'chart-tooltip')
            .style('opacity', 0)
            .style('position', 'absolute')
            .style('padding', '10px')
            .style('background', 'rgba(0,0,0,0.8)')
            .style('color', 'white')
            .style('border-radius', '5px')
            .style('pointer-events', 'none')
            .style('font-size', '12px')
            .style('z-index', '9999');
        
        this.tooltips.set(`tooltip-${containerId}`, tooltip);
        
        // Bind methods for use in event handlers
        const formatValue = this.formatValue.bind(this);
        const getElementLabel = this.getElementLabel.bind(this);
        
        // Add invisible rect for tracking mouse movement
        g.append('rect')
            .attr('width', width)
            .attr('height', height)
            .style('fill', 'none')
            .style('pointer-events', 'all')
            .on('mousemove', function(event) {
            const [mx] = d3.pointer(event);
            const date = x.invert(mx);
            
            
            // Dynamic format for tooltip based on data type
            let dateFormat;
            if (chartConfig.period === 'hours') {
                // Include full date and time for hourly
                dateFormat = d3.timeFormat('%B %d, %Y %H:%M');
            } else {
                // Full date for daily
                dateFormat = d3.timeFormat('%A, %B %d, %Y');
            }
            
            let tooltipContent = `<strong>${dateFormat(date)}</strong><br/>`;
            
            
            chartConfig.elements.forEach((elementId, index) => {
                const data = allData[elementId];
                if (!data) return;
                
                // Find closest point
                const bisect = d3.bisector(d => d.date).left;
                const i = bisect(data, date, 1);
                const d0 = data[i - 1];
                const d1 = data[i];
                const d = d1 && (date - d0.date > d1.date - date) ? d1 : d0;
                
                if (d) {
                const config = chartConfig.elementConfigs?.[elementId] || {};
                const color = config.color || 
                      this.defaultElementColors[elementId] || 
                      this.fallbackColors[index % this.fallbackColors.length];
               
                const y = config.axis === 'right' ? yRight : yLeft;
                
                // Format value properly
                const value = formatValue(d.value, elementId);
                tooltipContent += `<span style="color: ${color}">● ${getElementLabel(elementId)}: ${value}</span><br/>`;
                }
            });
            
            tooltip.html(tooltipContent)
                .style('left', (event.pageX + 10) + 'px')
                .style('top', (event.pageY - 28) + 'px')
                .style('opacity', 0.9);
            })
            .on('mouseout', function() {
            tooltip.style('opacity', 0);
            });
        
        // Also add hover to individual elements for precision
        chartConfig.elements.forEach((elementId, index) => {
            const color = colors[index % colors.length];
            
            // Add hover to dots if they exist
            g.selectAll(`.dot-${elementId}`)
            .style('cursor', 'pointer')
            .on('mouseover', function(event, d) {
                d3.select(this).attr('r', 5);
                tooltip.transition().duration(200).style('opacity', .9);
                const value = formatValue(d.value, elementId);
                tooltip.html(`<strong>${d.label || d3.timeFormat('%m/%d %H:%M')(d.date)}</strong><br/>
                            <span style="color: ${color}">● ${getElementLabel(elementId)}: ${value}</span>`)
                .style('left', (event.pageX + 10) + 'px')
                .style('top', (event.pageY - 28) + 'px');
            })
            .on('mouseout', function() {
                d3.select(this).attr('r', 2);
                tooltip.transition().duration(500).style('opacity', 0);
            });
            
            // Add hover to bars if they exist
            g.selectAll(`.bar-${elementId}`)
            .style('cursor', 'pointer')
            .on('mouseover', function(event, d) {
                d3.select(this).attr('opacity', 1);
                tooltip.transition().duration(200).style('opacity', .9);
                const value = formatValue(d.value, elementId);
                tooltip.html(`<strong>${d.label || d3.timeFormat('%m/%d')(d.date)}</strong><br/>
                            <span style="color: ${color}">● ${getElementLabel(elementId)}: ${value}</span>`)
                .style('left', (event.pageX + 10) + 'px')
                .style('top', (event.pageY - 28) + 'px');
            })
            .on('mouseout', function() {
                d3.select(this).attr('opacity', 0.7);
                tooltip.transition().duration(500).style('opacity', 0);
            });
        });
    },
    renderChart(chartConfig) {
      // Ensure we have the elements array
      if (!chartConfig.elements && chartConfig.element) {
        chartConfig.elements = [chartConfig.element];
      }
      
      // Get data for all elements
      const allData = {};
      let hasData = false;
      
      chartConfig.elements.forEach(elementId => {
        const data = this.getData(chartConfig.period, elementId);
        if (data && data.length > 0) {
          allData[elementId] = data;
          hasData = true;
        }
      });
      
      if (!hasData) {
        console.warn('No data for chart:', chartConfig);
        return;
      }
      
    // Calculate stats for ALL elements (not just single)
    chartConfig.elementStats = this.calculateAllElementStats(allData, chartConfig);
        
    // Remove old single stats property
    delete chartConfig.stats;
      
      // Clear existing chart and tooltips
      const tooltipKey = `tooltip-${chartConfig.id}`;
      if (this.tooltips.has(tooltipKey)) {
        this.tooltips.get(tooltipKey).remove();
        this.tooltips.delete(tooltipKey);
      }
      
      // Clear the container
      const container = d3.select(`#chart-${chartConfig.id}`);
      container.selectAll('*').remove();
      
      // Wait for DOM to update before rendering
      requestAnimationFrame(() => {
        this.renderMixedChart(chartConfig.id, allData, chartConfig);
        
      });
    },
    
    toggleFullscreen(chartId) {
      const element = document.querySelector(`#chart-${chartId}`)?.closest('.chart-container');
      if (!element) return;
      
      if (!document.fullscreenElement) {
        element.requestFullscreen().catch(err => {
          console.error('Fullscreen failed:', err);
        });
      } else {
        document.exitFullscreen();
      }
    },
    
    downloadChart(chartId) {
      const svg = document.querySelector(`#chart-${chartId} svg`);
      if (!svg) return;
      
      const svgData = new XMLSerializer().serializeToString(svg);
      const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
      const svgUrl = URL.createObjectURL(svgBlob);
      
      const downloadLink = document.createElement('a');
      downloadLink.href = svgUrl;
      downloadLink.download = `weather-chart-${chartId}.svg`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      URL.revokeObjectURL(svgUrl);
    },
    
        
    exportDashboard() {
        // Get current dashboard state
        const currentSettings = {
            gridColumns: this.gridColumns,
            layoutMode: this.layoutMode,
            enableTooltip: this.enableTooltip,
            enableDragDrop: this.enableDragDrop
        };
        
        // Get clean charts
        const cleanCharts = this.charts.map(chart => {
            const cleanChart = {
                id: chart.id,
                title: chart.title || undefined, // Only include if set
                elements: chart.elements || [chart.element],
                period: chart.period,
                showGrid: chart.showGrid !== false ? undefined : false, // Only include if false
            };
            
            // Add layout props only if custom mode and not default values
            if (this.layoutMode === 'custom') {
                if (chart.colSpan && chart.colSpan !== 1) cleanChart.colSpan = chart.colSpan;
                if (chart.rowSpan && chart.rowSpan !== 1) cleanChart.rowSpan = chart.rowSpan;
            }
            
            // Add element configs only if they have non-default values
            if (chart.elementConfigs && Object.keys(chart.elementConfigs).length > 0) {
                const configs = {};
                Object.entries(chart.elementConfigs).forEach(([elementId, config]) => {
                    const cleanConfig = {};
                    if (config.chartType && config.chartType !== 'line') {
                        cleanConfig.chartType = config.chartType;
                    }
                    if (config.axis && config.axis !== 'left') {
                        cleanConfig.axis = config.axis;
                    }
                    if (config.showStats === false) {
                        cleanConfig.showStats = false;
                    }
                    if (config.color && config.color !== this.getDefaultElementColor(elementId)) {
                        cleanConfig.color = config.color;
                    }
                    
                    if (Object.keys(cleanConfig).length > 0) {
                        configs[elementId] = cleanConfig;
                    }
                });
                
                if (Object.keys(configs).length > 0) {
                    cleanChart.elementConfigs = configs;
                }
            }
            
            return cleanChart;
        });
        
        const exportData = {
            version: '1.0',
            settings: currentSettings,
            charts: cleanCharts,
            exportDate: new Date().toISOString(),
            // Add metadata
            metadata: {
                chartsCount: cleanCharts.length,
                location: this.weatherData?.address || 'Unknown'
            }
        };
        
        // Create formatted JSON
        const blob = new Blob([JSON.stringify(exportData, null, 2)], 
            { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        
        const downloadLink = document.createElement('a');
        downloadLink.href = url;
        downloadLink.download = `weather-dashboard-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        URL.revokeObjectURL(url);
    },

    hasVisibleStats(chart) {
        if (!chart.elementConfigs) return true; // Default to show if no configs
        
        return chart.elements.some(elementId => {
            const config = chart.elementConfigs[elementId];
            return !config || config.showStats !== false; // Default to true
        });
    },
    
    shouldShowElementStats(chart, elementId) {
        if (!chart.elementConfigs || !chart.elementConfigs[elementId]) {
            return true; // Default to show stats
        }
        return chart.elementConfigs[elementId].showStats !== false;
    },
  },
 created() {
    // Initialize local package definition
    this.localPackageDefn = this.createLocalPackageDefn();
  },
  
  mounted() {
    // Load dashboard definition
    this.loadDashboardDefinition();
    
    // Create ResizeObserver to watch for container size changes
    this.resizeObserver = new ResizeObserver(entries => {
        if (this.resizeDebounce) clearTimeout(this.resizeDebounce);
        
        this.resizeDebounce = setTimeout(() => {
            entries.forEach(entry => {
                const chartId = entry.target.id;
                const chart = this.charts.find(c => `chart-${c.id}` === chartId);
                if (chart) {
                    this.renderChart(chart);
                }
            });
        }, 250);
    });
    
    // Initialize charts after definition is loaded
    this.$nextTick(() => {
        // If charts exist from saved definition, validate and render them
        if (this.charts.length > 0) {
            console.log('Validating loaded charts with current weather data');
            this.refreshAllChartsWithNewData();
        } 
        // Only add default chart if no saved charts and data is available
        else if (this.weatherData && this.availablePeriods.length > 0) {
            this.selectedPeriod = this.availablePeriods[0];
            const firstGroup = Object.values(this.elementGroups)[0];
            if (firstGroup && firstGroup.length > 0) {
                this.selectedElement = firstGroup[0].id;
                // Optionally auto-add first chart
                // this.addChart();
            }
        }
    });
    
    // Handle window resize
    this.resizeHandler = () => {
        if (this.windowResizeDebounce) clearTimeout(this.windowResizeDebounce);
        
        this.windowResizeDebounce = setTimeout(() => {
            this.refreshAllCharts();
        }, 300);
    };
    window.addEventListener('resize', this.resizeHandler);
  },
  
  beforeUnmount() {
    // Save final state before unmounting
    this.saveDashboardDefinition();
    // Clean up all tooltips
    this.tooltips.forEach(tooltip => tooltip.remove());
    this.tooltips.clear();
    
    // Clean up resize observer
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    
    // Clean up timeouts
    if (this.refreshTimeout) clearTimeout(this.refreshTimeout);
    if (this.resizeDebounce) clearTimeout(this.resizeDebounce);
    if (this.windowResizeDebounce) clearTimeout(this.windowResizeDebounce);
    
    // Clean up resize handler
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }
    
    // Clean up any remaining tooltips in DOM
    d3.selectAll('.chart-tooltip').remove();
  }
};
</script>

<style scoped>
.chart-title {
  display: inline-block;
  max-width: 80%;
  word-break: break-word;  /* Break long words */
  overflow-wrap: break-word; /* Alternative property for compatibility */
  hyphens: auto; /* Add hyphens where possible */
}
/* Essential positioning for dropdown */
.add-element-dropdown {
  position: relative;
}

.add-element-dropdown .dropdown-menu {
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 0.25rem;
  z-index: 1050;
}

/* Chart container basics */
.chart-container {
  min-height: 400px;
  position: relative;
  transition: all 0.3s ease;
}

.chart-container.editing {
  border: 2px solid #0d6efd;
}

.chart-container.new-chart {
  border: 2px dashed #28a745;
  background: rgba(40, 167, 69, 0.02);
}

.chart-container.dragging {
  opacity: 0.5;
  transform: scale(0.98);
}

.chart-container.drag-over {
  border: 2px dashed #0d6efd;
  background: rgba(13, 110, 253, 0.05);
}

/* Chart area */
.chart-area {
  width: 100%;
  min-height: 300px;
  position: relative;
}

/* Stats display */
.stats-container {
  max-height: 400px;
}

.stats-color-indicator {
  width: 12px;
  height: 12px;
  border-radius: 2px;
  display: inline-block;
}

/* Animations */
.chart-list-move,
.chart-list-enter-active,
.chart-list-leave-active {
  transition: all 0.3s ease;
}

.chart-list-enter-from,
.chart-list-leave-to {
  opacity: 0;
  transform: scale(0.9);
}

/* D3 chart specific styles */
:deep(.grid line) {
  stroke: #e0e0e0;
}

:deep(.grid path) {
  stroke-width: 0;
}

:deep(.bar) {
  transition: fill 0.2s;
  cursor: pointer;
}

:deep(.dot) {
  cursor: pointer;
  transition: all 0.2s;
}

:deep(.x-axis text),
:deep(.y-axis text) {
  font-size: 12px;
}

:deep(svg) {
  max-width: 100%;
  height: auto;
}

/* Animation for new charts */
@keyframes pulse-border {
  0%, 100% { border-color: #28a745; }
  50% { border-color: #0d6efd; }
}

.chart-container.editing.new-chart {
  animation: pulse-border 2s infinite;
}

/* Slide animation for advanced options */
.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}
.form-control-color {
    width: 40px;
    padding: 0.25rem;
    height: 31px;
}

.input-group-sm .form-control-color {
    border-radius: 0.25rem 0 0 0.25rem;
}

/* Element settings layout */
.element-settings .row > div {
    display: flex;
    align-items: center;
}

.element-settings .input-group-sm {
    width: 100%;
}
    /* Force Bootstrap button check styles if they're not applying */
.btn-check {
    position: absolute;
    clip: rect(0,0,0,0);
    pointer-events: none;
}

.btn-check:checked + .btn {
    color: var(--bs-btn-active-color);
    background-color: var(--bs-btn-active-bg);
    border-color: var(--bs-btn-active-border-color);
}

.btn-check:checked + .btn-outline-success {
    color: #fff;
    background-color: #198754;
    border-color: #198754;
}

.btn-check:checked + .btn-outline-danger {
    color: #fff;
    background-color: #dc3545;
    border-color: #dc3545;
}

.btn-check:checked + .btn-outline-warning {
    color: #000;
    background-color: #ffc107;
    border-color: #ffc107;
}
</style>